import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { StagingBadge } from "@cinesys/ui/components";
import CinesysLogo, { CINESYS_URL } from "./CinesysLogo";
import { useCustomerLogoUrl } from "../hooks/useCustomerContext";

/** Same breakpoint `SpotterHeader`/`MetadataImportHeader` hide their own "Powered by" badge at
 *  (`AbHeader` has no such breakpoint — its own "Powered by" badge never hides on mobile). */
const MOBILE_MAX_WIDTH = "700px";

/**
 * The shell header for customer-apps' own baseline surfaces ONLY (home, item-browser, browse,
 * collections, saved searches, search history, jobs, item-detail, collections-browser) — mounted
 * above every route nested under `ShellLayout`. AB Vault, Spotter, and Metadata Import each render
 * their OWN independent header (`AbHeader`/`SpotterHeader`/`MetadataImportHeader`) and are not
 * nested under `ShellLayout` at all — see `docs/decisions.md` for why.
 *
 * Same background as the page body now (`CustomerLayout.tsx`'s `Shell`, same literal value) —
 * Addison: "make the body the same color as the header" (2026-09-08 interactive session). Page
 * content floats on top of that shared dark surface in its own `<Section>` card instead of
 * relying on a body-vs-header color step for depth. No product-switcher dropdown, no
 * product-name text, no org-name breadcrumb, the org's own iconik logo rendered larger, "Powered
 * by Cinesys" on the right.
 */
const Bar = styled.header`
  /* A literal, not a --dark* token: --dark060 is already the darkest step in both palettes this
     app uses (default @cinesys/ui and cinesysExperienceColors), so matching the body (which
     uses this same literal, see CustomerLayout.tsx's Shell) needs a value below that floor
     rather than an existing token. Keep these two literals in sync if this ever changes. */
  background: #0a0b0d;
  color: #fff;
  /* 5px vertical (was 12px) — several baseline routes (Wrapper.tsx/CreatePlaceholderPage.tsx)
     hardcode calc(100vh - 50px), assuming this bar renders at exactly 50px tall (its pre-#211
     height); #211's redesign grew it past that without updating those constants, which is what
     caused the page-level scroll this round is fixing. 40px logo + 5px top/bottom keeps the bar
     at that same 50px budget instead of touching those unrelated hardcoded values (see
     docs/decisions.md). */
  padding: 5px 15px;
  display: flex;
  align-items: center;
  gap: 14px;
  position: sticky;
  top: 0;
  z-index: 40;
`;

const Lockup = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const MarkLink = styled(Link)`
  display: flex;
  align-items: center;
  line-height: 0;
`;

/** The org's own logo, pulled from iconik. Same box as `CinesysLogo`'s mark so swapping between
 *  the two (org logo present vs. the CineSys-mark fallback) doesn't jump the bar's height. Bumped
 *  again from 34px — Addison: still too small to read as the org's own mark. */
const OrgLogoImg = styled.img`
  height: 40px;
  width: auto;
  display: block;
`;

const Spacer = styled.div`
  flex: 1 1 auto;
`;

/** Right-aligned CineSys attribution — same badge shape as `SpotterHeader`/`AbHeader`/
 *  `MetadataImportHeader`; the `CinesysLogo` height below now matches theirs too (28px, was 18px,
 *  then 34px — see docs/decisions.md). */
const PoweredBy = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  text-decoration: none;
  opacity: 0.85;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}) {
    display: none;
  }
`;

const PoweredByLabel = styled.span`
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--headerCrumbMuted);
  white-space: nowrap;
`;

/**
 * Resolves what to render as the top-left mark: the org's own iconik logo when
 * `useCustomerLogoUrl` has one, else `null` so the caller falls back to the CineSys mark. Kept as
 * a standalone, directly-testable function — a blank/whitespace-only `logoUrl` is treated the
 * same as absent rather than rendering a broken `<img>`.
 */
export function resolveHeaderLogoSrc(
  logoUrl: string | null | undefined
): string | null {
  return logoUrl && logoUrl.trim() ? logoUrl : null;
}

const ShellHeader = () => {
  const { data: logo } = useCustomerLogoUrl();
  const orgLogoSrc = resolveHeaderLogoSrc(logo?.logoUrl);

  return (
    <Bar>
      <Lockup>
        <MarkLink to="/" aria-label="Home">
          {orgLogoSrc ? (
            <OrgLogoImg src={orgLogoSrc} alt="" />
          ) : (
            <CinesysLogo height={40} />
          )}
        </MarkLink>
        <StagingBadge />
      </Lockup>
      <Spacer />
      <PoweredBy href={CINESYS_URL} target="_blank" rel="noopener noreferrer">
        <PoweredByLabel>Powered by</PoweredByLabel>
        <CinesysLogo height={28} />
      </PoweredBy>
    </Bar>
  );
};

export default ShellHeader;
